import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Box, Button, Menu, MenuItem, Tooltip } from '@mui/material'
import { Call as CallIcon, Videocam as VideocamIcon, Mic as MicIcon } from '@mui/icons-material'
import { ROUTES } from '@/utils/constants'
import { useLessonCall } from '@/webrtc/callContext'

/**
 * Кнопка звонка в строке действий урока.
 *
 * Учителю — «Позвонить», участникам идущего звонка — «Присоединиться»: без неё
 * ученик, однажды отклонивший вызов, мог бы вернуться только перезагрузкой.
 *
 * Когда звонки не входят в тариф, кнопка не прячется, а ведёт к подписке:
 * скрытая функция выглядит как её отсутствие, и учитель не узнает, что она есть.
 */
export function LessonCallButton({ canStart }: { canStart: boolean }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const call = useLessonCall()
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)

  if (call.status !== 'idle') return null

  // Звонок уже идёт — предлагаем войти, а не начать новый (второй нельзя).
  if (call.session?.is_active) {
    return (
      <Button
        variant="outlined"
        size="small"
        color="secondary"
        startIcon={<CallIcon />}
        onClick={() => void call.acceptCall()}
      >
        {t('lessonCall.actions.join')}
      </Button>
    )
  }

  if (!canStart) return null

  const audioAllowed = call.config?.audio.enabled ?? false
  const videoAllowed = call.config?.video.enabled ?? false
  const callsDisabledInPlan = call.config !== null && !audioAllowed && !videoAllowed

  if (callsDisabledInPlan) {
    return (
      <Tooltip title={t('lessonCall.upgradeHint')}>
        <Button
          variant="outlined"
          size="small"
          color="secondary"
          startIcon={<CallIcon />}
          onClick={() => navigate(ROUTES.PROFILE_SUBSCRIPTION)}
        >
          {t('lessonCall.actions.start')}
        </Button>
      </Tooltip>
    )
  }

  // Есть только аудио — меню из одного пункта не нужно.
  if (!videoAllowed) {
    return (
      <Button
        variant="outlined"
        size="small"
        color="secondary"
        startIcon={<CallIcon />}
        onClick={() => void call.startCall('audio')}
      >
        {t('lessonCall.actions.start')}
      </Button>
    )
  }

  return (
    <Box component="span">
      <Button
        variant="outlined"
        size="small"
        color="secondary"
        startIcon={<CallIcon />}
        onClick={(event) => setMenuAnchor(event.currentTarget)}
      >
        {t('lessonCall.actions.start')}
      </Button>
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        <MenuItem
          disabled={!audioAllowed}
          onClick={() => {
            setMenuAnchor(null)
            void call.startCall('audio')
          }}
        >
          <MicIcon fontSize="small" sx={{ mr: 1 }} />
          {t('lessonCall.actions.startAudio')}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setMenuAnchor(null)
            void call.startCall('video')
          }}
        >
          <VideocamIcon fontSize="small" sx={{ mr: 1 }} />
          {t('lessonCall.actions.startVideo')}
        </MenuItem>
      </Menu>
    </Box>
  )
}
